/**
Project: Voice Panel (c)
Title: Users 
Description: Component to verify the users creation.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Text, Button } from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import PageUtil from "../../../../core/helpers/page-util";
import IntlUtil from "../../../../core/helpers/intl-util";
import ResponseUtil from "../../../../core/helpers/response-util";
import AlertInfoMessage from "../../common/helpers/alert-info-message";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import { AppConfigProps } from "../../../../core/settings/app-config";
import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import { manageError } from "../../../../core/actions/common-actions";
import {
  showPageWaitDialog,
  hidePageWaitDialog,
} from "../../common/actions/general-actions";
import { setVoiceSetupProgressPage } from "../actions/voice-setup-actions";
import {
  getUsers,
  getUsersFileData,
  uploadUsers,
} from "../../users/actions/user-actions";
import TokenUtil from "../../../../core/helpers/token-util";
import PageExitGuard from "../../common/helpers/page-exit-guard";

class Users extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_voicesetup = "vpa_voicesetup";
  _intl_ns_vpa_users = "vpa_users";

  constructor(props) {
    super(props);
    this.state = {
      isPageDataFetched: false,
      isFormDataFilled: false,
      usersAllRecords: [],
      showExportStatusDialog: false,
      showAlert: false,
      successMessage: null,
      errorMessage: null,
      errorDetails: null,
      isError: false,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    await this.props.hidePageWaitDialog();
    // await this.loadPageData();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  // loadPageData = async () => {
  //   try {
  //     await this.setStateAsync({ isPageDataFetched: false });
  //     await this.setStateAsync({ isPageDataFetched: true });
  //   } catch (err) {
  //     await manageError(err, this.props.history);
  //   }
  // };

  loadNextPage = async () => {
    await this.props.setVoiceSetupProgressPage(
      VoiceAdminConstants.VOICE_SETUP.PROGRESS.SUMMARY
    );
  };

  fetchUsers = async () => {
    await getUsers(
      null,
      this._cancelToken
    )
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data &&
          res.data.records
        ) {
          await this.setStateAsync({ usersAllRecords: res.data.records });
        } else {
          await this.setState({ isError: true });
          await manageError(res, this.props.history);
        }
      })
      .catch(async (err) => {
        await this.setState({ isError: true });
        await manageError(err, this.props.history);
      });
  };

  exportUsers = async () => {
    await this.props.showPageWaitDialog(
      IntlUtil.getText(
        this._intl_ns_vpa_users,
        "notification.info.pleaseWaitExportingData"
      )
    );
    await this.setStateAsync({ showExportStatusDialog: false });
    await this.fetchUsers();

    const csvFileData = getUsersFileData(
      this._intl_ns_vpa_users,
      this.state.usersAllRecords
    );
    const csvFileName = "users.csv";
    PageUtil.downloadCSVFile(csvFileName, csvFileData);
    await setTimeout(async () => {
      await this.setStateAsync({ showExportStatusDialog: true });
      await this.props.hidePageWaitDialog();
    }, 5000);
  };

  handleFileInputClick = (e) => {
    e.target.value = "";
  };

  importUsers = async (e) => {
    await this.clearAlert();
    const file = e.target.files[0];
    if (file) {
      const fileData = new FormData();
      fileData.append("file", file);
      const headerData = {
        authorization: TokenUtil.getIdentitySippioAccessToken(),
        refreshToken: TokenUtil.getIdentitySippioRefreshToken(),
      };

      await this.props.showPageWaitDialog(
        IntlUtil.getText(
          this._intl_ns_vpa_users,
          "notification.info.pleaseWaitUpdatingData"
        )
      );

      await uploadUsers(headerData, fileData, this._cancelToken)
        .then(async (res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.result
          ) {
            await this.setStateAsync({
              successMessage: IntlUtil.getText(
                this._intl_ns_vpa_users,
                "notification.success.usersDataUpload"
              ),
              showAlert: true,
            });
          } else {
            await this.props.hidePageWaitDialog();
            await this.setState({ isError: true });
            await manageError(new Error(), this.props.history);
          }
        })
        .catch(async (err) => {
          await this.props.hidePageWaitDialog();
          const errorStatus = ResponseUtil.getStatus(err);
          if (
            errorStatus &&
            errorStatus === AppConfigProps.httpStatusCode.badRequest
          ) {
            const primeError = ResponseUtil.getPrimeError(err);
            if (
              primeError &&
              primeError.code ===
              VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
            ) {
              this.props.history.push(VoiceAdminURLProps.setup);
              return;
            }

            if (
              primeError &&
              (primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE
                  .USERS_DATA_UPDATE_FAILED ||
                primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE
                  .USERS_IMPORT_FILE_INVALID ||
                primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE
                  .USERS_IMPORT_FILE_NOCHANGES)
            ) {
              await this.setStateAsync({
                errorDetails: primeError.message,
              });
            }

            await this.setStateAsync({
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_users,
                "notification.error.usersDataUpdateFailed"
              ),
              showAlert: true,
            });
          } else {
            await this.setState({ isError: true });
            await manageError(err, this.props.history);
          }
        });
      await this.props.hidePageWaitDialog();
    }
  };

  clearAlert = async () => {
    await this.setStateAsync({
      showAlert: false,
      successMessage: false,
      errorMessage: null,
      errorDetails: null,
    });
  };

  render() {
    return (
      <Grid fluid className="page-grid m-t-15">
        <Row>
          <Col xs={12} className="m-b-20">
            <Text className="page-heading4">
              {IntlUtil.getText(
                this._intl_ns_vpa_voicesetup,
                "content.users.activateUsers"
              )}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="m-b-20">
            <Text className="page-text">
              {IntlUtil.getText(
                this._intl_ns_vpa_voicesetup,
                "content.users.userActivationInfo1"
              )}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="m-b-10">
            <Text className="page-text">
              {IntlUtil.getText(
                this._intl_ns_vpa_voicesetup,
                "content.users.userActivationInfo2"
              )}
            </Text>
          </Col>
        </Row>
        {/* <Row>
          <Col xs={12} className="m-b-10">
            <Text className="page-text">
              {IntlUtil.getText(
                this._intl_ns_vpa_voicesetup,
                "content.users.importUsersInfo"
              )}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="m-b-20">
            <Text className="page-text">
              {IntlUtil.getText(
                this._intl_ns_vpa_voicesetup,
                "content.users.exportUsersInfo"
              )}
            </Text>
          </Col>
        </Row> */}
        {this.state.showAlert === true ? (
          this.state.errorMessage ? (
            <Row>
              <Col xs={12}>
                <AlertErrorMessage
                  content={this.state.errorMessage}
                  moreDetails={{
                    label: IntlUtil.getText(
                      this._intl_ns_vpa_users,
                      "content.moreDetails"
                    ),
                    content: this.state.errorDetails,
                  }}
                />
              </Col>
            </Row>
          ) : (
            <Row>
              <Col xs={12}>
                <AlertInfoMessage
                  fitted={true}
                  content={this.state.successMessage}
                />
              </Col>
            </Row>
          )
        ) : null}
        <Row className="m-t-20">
          {/* <Col className="m-r-20 m-b-20">
            <Button
              primary
              // disabled={!this.state.isFormDataFilled}
              className="vpa-usn-button-file m-l-5"
            >
              <label>
                <span>
                  {IntlUtil.getText(
                    this._intl_ns_vpa_users,
                    "content.importUsers"
                  )}
                </span>
                <input
                  id="importUserFile"
                  type="file"
                  accept=".csv"
                  onClick={this.handleFileInputClick}
                  onChange={(e) => {
                    e.persist();
                    this.importUsers(e);
                  }}
                />
              </label>
            </Button>
          </Col> */}
          {/* <Col className="m-r-20 m-b-20">
            <Button
              primary
              content={IntlUtil.getText(
                this._intl_ns_vpa_users,
                "content.exportUsers"
              )}
              // disabled={!this.state.isFormDataFilled}
              onClick={this.exportUsers}
              className="vpa-voice-setup-button m-l-5"
            />
            <Dialog
              backdrop={true}
              confirmButton={IntlUtil.getText(
                this._intl_ns_vpa_users,
                "content.ok"
              )}
              onConfirm={() => {
                this.setState({ showExportStatusDialog: false });
              }}
              open={this.state.showExportStatusDialog}
              content={
                <Text
                  className="vpa-page-dialog-info-text"
                  content={IntlUtil.getText(
                    this._intl_ns_vpa_users,
                    "notification.info.exportUsers"
                  )}
                />
              }
              className="vpa-page-dialog"
            />
          </Col> */}
          <Col className="m-r-20 m-b-20">
            <Button
              primary
              content={IntlUtil.getText(
                this._intl_ns_vpa_voicesetup,
                "content.continueSetup"
              )}
              // disabled={!this.state.isFormDataFilled}
              onClick={this.loadNextPage}
              className="vpa-voice-setup-button m-l-5"
            />
            <PageExitGuard
              when={this.state.isError === false}
              navigate={(path) => this.props.history.push(path)}
              shouldBlockNavigation={(location) => {
                if (location.pathname) {
                  return true;
                }
                return false;
              }}
            />
          </Col>
        </Row>
      </Grid>
    );
  }
}

const mapStateToProps = (state) => ({
  voiceSetupProgressPage: state.voiceSetupStore.voiceSetupProgressPage,
  subscriberAccount: state.subscriberStore.subscriberAccount,
});

const mapActionToProps = {
  setVoiceSetupProgressPage,
  showPageWaitDialog,
  hidePageWaitDialog,
};

export default connect(mapStateToProps, mapActionToProps)(Users);
